import type {
  Currency,
  OrderId,
  PlanningPageQuery,
  VoyageCharterId,
  VoyageIncomeQuery,
  VoyageIncomeQuery_VoyageCharterFragment,
} from '../../graphql';

import {
  VoyageIncomeDocument,
  useExtraFreightUpdatedSubscription,
  useAllWorldscaleFreightsQuery,
} from '../../graphql';

import { useCurrentUser, useDefaultCurrency } from '../../state/zustand';
import { usePortCosts } from './usePortCost';
import type { PortCostEstimateMaps } from './usePortCostEstimates';
import { usePortCostsCoa } from './usePortCostCoa';
import { useMemo } from 'react';
import { cloneDeep, compact } from 'lodash';
import type { CoaPrice, CoaPriceIndex } from '../contracts/coa_prices';
import { buildCoaPriceIndex } from '../contracts/coa_prices';
import { mapKeyBy, withDefined } from '../../lib/util';
import {
  buildWorldscaleFreightRateMap,
  type WorldscaleFreightRateMap,
} from '../../lib/worldscaleFreight';
import { DefaultMap } from '@seaber/common/lib/collections';
import type { ScenarioOrder } from '../../lib/planningData';
import type {
  CoaRoute,
  DeviationCostEntry,
} from '../contracts/coa_deviation_costs';
import type { DeviationCostIndexByCoaId } from '../pnl/coa/deviationCostProcessor';
import type { AdditionalPortCostIndexByCoaId } from '../pnl/coa/additionalPortCostProcessor';
import type {
  ChartererPortCostIndexByCoaId,
  ChartererPortCostRow,
} from '../pnl/coa/chartererPortCostProcessor';
import type { LocationId, ZoneId } from '@seaber/common/graphql/types';

export type IndexedCoaPricesByCoaId = Map<number, CoaPriceIndex>;

export type PnlData = {
  additionalPortCostIndexByCoaId: AdditionalPortCostIndexByCoaId;
  chartererPortCostIndexByCoaId: ChartererPortCostIndexByCoaId;
  deviationCostIndexByCoaId: DeviationCostIndexByCoaId;
  incomingVoyageChartersByOrderId: Map<
    OrderId,
    VoyageIncomeQuery_VoyageCharterFragment
  >;
  incomingVoyageChartersById: Map<
    VoyageCharterId,
    VoyageIncomeQuery_VoyageCharterFragment
  >;
  indexedCoaPricesByCoaId: IndexedCoaPricesByCoaId;
  outgoingVoyageChartersById: Map<
    VoyageCharterId,
    VoyageIncomeQuery_VoyageCharterFragment
  >;
  portCostByVcMaps: ReturnType<typeof usePortCosts>;
  portCostEstimateMaps: PortCostEstimateMaps;
  portCostByCoaMaps: ReturnType<typeof usePortCostsCoa>;
  worldscaleFreightRates: WorldscaleFreightRateMap;
};

export function usePnlData(
  orderById: Map<number, ScenarioOrder>,
  data: PlanningPageQuery | undefined,
  portCostEstimateMaps: PortCostEstimateMaps,
): PnlData {
  const currentUser = useCurrentUser();
  const currentUserOrg = currentUser?.organizationId;
  const shipownerOrgId = currentUser?.organizationId ?? null;
  const defaultCurrency = useDefaultCurrency();

  const { data: worldscaleData } = useAllWorldscaleFreightsQuery({
    skip: !currentUserOrg,
  });

  const worldscaleFreightRates = useMemo(
    () =>
      buildWorldscaleFreightRateMap(
        worldscaleData?.allWorldscaleFreights?.nodes,
      ),
    [worldscaleData],
  );

  const voyageCharterMaps = useMemo(
    () => getVoyageCharterPnlMaps(orderById, shipownerOrgId),
    [orderById, shipownerOrgId],
  );

  const extraFreightOptions = useMemo(() => {
    const options: Parameters<typeof useExtraFreightUpdatedSubscription>[0] = {
      variables: {
        org: `${currentUserOrg}`,
      },
      skip: currentUserOrg == null,
      onData: async (options) => {
        const { data, client } = options;
        const { event, extraFreight } =
          data.data?.voyageCharterExtraFreightUpdated || {};
        const dataInStore = client.readQuery<VoyageIncomeQuery>({
          query: VoyageIncomeDocument,
        });
        if (!dataInStore) return;

        const next = cloneDeep(dataInStore);
        const allVoyageCharters = next.allVoyageCharters?.nodes;
        if (!allVoyageCharters) return;

        if (extraFreight) {
          const voyageCharterIndex = allVoyageCharters.findIndex(
            (node) => node?.id === extraFreight.voyageCharterId,
          );
          if (voyageCharterIndex === -1) return;
          const voyageCharter = allVoyageCharters[voyageCharterIndex];
          const extraFreights =
            voyageCharter?.voyageCharterExtraFreightsByVoyageCharterId.nodes;
          if (!extraFreights) return;
          const extraFreightIndex = extraFreights.findIndex(
            (ef) => ef?.id === extraFreight.id,
          );
          // Insert
          if (extraFreightIndex === -1) {
            extraFreights.push(extraFreight);
            client.writeQuery<VoyageIncomeQuery>({
              query: VoyageIncomeDocument,
              data: {
                ...next,
              },
            });
            return;
          }
          // Update
          else {
            extraFreights[extraFreightIndex] = {
              ...extraFreights[extraFreightIndex],
              ...extraFreight,
            };

            client.writeQuery<VoyageIncomeQuery>({
              query: VoyageIncomeDocument,
              data: {
                ...next,
              },
            });
            return;
          }
        } else if (event) {
          // Delete extra freight
          for (const vc of allVoyageCharters) {
            const extraFreights =
              vc?.voyageCharterExtraFreightsByVoyageCharterId.nodes;
            if (!extraFreights || !extraFreights.length) continue;
            const extraFreightIndex = extraFreights.findIndex(
              (ef) => ef?.id === Number(event),
            );
            if (extraFreightIndex !== -1) {
              extraFreights.splice(extraFreightIndex, 1);
              client.writeQuery<VoyageIncomeQuery>({
                query: VoyageIncomeDocument,
                data: {
                  ...next,
                },
              });
              return;
            }
          }
        }
      },
    };
    return options;
  }, [currentUserOrg]);
  useExtraFreightUpdatedSubscription(extraFreightOptions);

  const indexedCoaPricesByCoaId = useMemo(
    () => getIndexedCoaPricesByCoaId(data, defaultCurrency),
    [defaultCurrency, data],
  );

  const deviationCostIndexByCoaId = useMemo(
    () => getDeviationCostIndexByCoaId(data, defaultCurrency),
    [defaultCurrency, data],
  );

  const additionalPortCostIndexByCoaId = useMemo(
    () => getAdditionalPortCostIndexByCoaId(data, defaultCurrency),
    [defaultCurrency, data],
  );

  const chartererPortCostIndexByCoaId = useMemo(
    () => getChartererPortCostIndexByCoaId(data, defaultCurrency),
    [defaultCurrency, data],
  );

  const portCostByVcMaps = usePortCosts();
  const portCostByCoaMaps = usePortCostsCoa();

  /** Memoized so that you can pass the whole returned object down the tree */
  const pnlData = useMemo(
    () => ({
      additionalPortCostIndexByCoaId,
      chartererPortCostIndexByCoaId,
      deviationCostIndexByCoaId,
      indexedCoaPricesByCoaId,
      portCostByVcMaps,
      portCostEstimateMaps,
      portCostByCoaMaps,
      worldscaleFreightRates,
      ...voyageCharterMaps,
    }),
    [
      additionalPortCostIndexByCoaId,
      chartererPortCostIndexByCoaId,
      deviationCostIndexByCoaId,
      indexedCoaPricesByCoaId,
      portCostByVcMaps,
      portCostEstimateMaps,
      portCostByCoaMaps,
      worldscaleFreightRates,
      voyageCharterMaps,
    ],
  );

  return pnlData;
}

export function getIndexedCoaPricesByCoaId(
  data: PlanningPageQuery | undefined,
  defaultCurrency: Currency,
) {
  const assortmentsByAssortmentGroupId = new DefaultMap<number, number[]>(
    () => [],
  );
  for (const a of data?.allAssortments?.nodes ?? []) {
    const groupId = a?.assortmentGroupByAssortmentGroup?.id;
    if (groupId) {
      assortmentsByAssortmentGroupId.get(groupId).push(a.id);
    }
  }

  // Zone members are location ids: getIndexedCoaPrice looks these up by the
  // cargo's load/discharge port id.
  const locationsByZoneId = getLocationIdsByZoneId(data);

  const map: IndexedCoaPricesByCoaId = new Map();

  for (const coa of data?.allCoaContracts?.nodes ?? []) {
    if (!coa) continue;
    const prices: CoaPrice[] = [];

    const coaAssortmentById = mapKeyBy(
      compact(coa.coaContractAssortmentsByCoaContractId.nodes),
      (ca) => ca.id,
    );

    for (const coaPrice of coa.coaContractPricesByCoaContractId.nodes) {
      if (!coaPrice?.coaContractPortByRouteId) continue;
      const coaAssortment = coaAssortmentById.get(coaPrice.coaAssortmentId);
      const portPair = coaPrice.coaContractPortByRouteId;
      if (!coaAssortment || !portPair) continue;

      prices.push({
        assortmentGroupId: coaAssortment.assortmentGroupId ?? null,
        assortmentId: coaAssortment.assortmentId ?? null,
        cargoUnit: coa.cargoUnitByAmountUnitId?.name ?? null,
        currency: coa.currency ?? defaultCurrency,
        dischargePortId: portPair.destinationId ?? null,
        dischargeZoneId: portPair.destinationZoneId ?? null,
        loadPortId: portPair.originId ?? null,
        loadZoneId: portPair.originZoneId ?? null,
        maxAmount: withDefined(coaAssortment.maxAmount, Number),
        minAmount: withDefined(coaAssortment.minAmount, Number),
        paymentUnit: coaAssortment.paymentUnit,
        price: +coaPrice.price,
      });
    }

    const index = buildCoaPriceIndex(
      prices,
      assortmentsByAssortmentGroupId,
      locationsByZoneId,
    );

    map.set(coa.id, index);
  }

  return map;
}

type PlanningCoaNode = NonNullable<
  NonNullable<PlanningPageQuery['allCoaContracts']>['nodes'][number]
>;

// Contract port pairs resolved to the route shape both cost processors consume to
// map load ports to ranges (zones).
function buildCoaRoutes(coa: PlanningCoaNode): CoaRoute[] {
  return compact(coa.coaContractPortsByCoaContractId.nodes).map((route) => ({
    originId: route.originId ?? null,
    originZoneId: route.originZoneId ?? null,
    zoneByOriginZoneId: route.zoneByOriginZoneId
      ? {
          zoneLocationsByZoneId: {
            nodes: compact(
              route.zoneByOriginZoneId.zoneLocationsByZoneId.nodes,
            ).map((zl) => ({ locationId: zl.locationId })),
          },
        }
      : null,
  }));
}

function buildDeviationCostEntries(coa: PlanningCoaNode): DeviationCostEntry[] {
  return compact(coa.coaContractDeviationCostsByCoaContractId?.nodes ?? []).map(
    (n) => ({
      fromZoneId: n.fromZoneId ?? null,
      toZoneId: n.toZoneId ?? null,
      fromLocationId: n.fromLocationId ?? null,
      toLocationId: n.toLocationId ?? null,
      lumpSumAmount: n.lumpSumAmount != null ? +n.lumpSumAmount : null,
    }),
  );
}

export function getDeviationCostIndexByCoaId(
  data: PlanningPageQuery | undefined,
  defaultCurrency: Currency,
): DeviationCostIndexByCoaId {
  const map: DeviationCostIndexByCoaId = new Map();

  for (const coa of data?.allCoaContracts?.nodes ?? []) {
    if (!coa) continue;

    const costs = buildDeviationCostEntries(coa);
    if (costs.length === 0) continue;

    map.set(coa.id, {
      costs,
      routes: buildCoaRoutes(coa),
      currency: coa.currency ?? defaultCurrency,
    });
  }

  return map;
}

export function getAdditionalPortCostIndexByCoaId(
  data: PlanningPageQuery | undefined,
  defaultCurrency: Currency,
): AdditionalPortCostIndexByCoaId {
  const map: AdditionalPortCostIndexByCoaId = new Map();

  for (const coa of data?.allCoaContracts?.nodes ?? []) {
    if (!coa) continue;

    map.set(coa.id, {
      freeLoadPorts: coa.freeLoadPorts,
      freeDischargePorts: coa.freeDischargePorts,
      standardLoadCost:
        coa.standardAdditionalLoadPortCost != null
          ? +coa.standardAdditionalLoadPortCost
          : null,
      standardDischargeCost:
        coa.standardAdditionalDischargePortCost != null
          ? +coa.standardAdditionalDischargePortCost
          : null,
      loadPortOverrides: new Map(
        compact(
          coa.coaContractLoadPortOverridesByCoaContractId?.nodes ?? [],
        ).map((n) => [n.locationId, +n.overrideCost]),
      ),
      currency: coa.currency ?? defaultCurrency,
      routes: buildCoaRoutes(coa),
      costs: buildDeviationCostEntries(coa),
    });
  }

  return map;
}

/**
 * Zone membership is a property of the zone, so it is read from allZones rather
 * than inferred from how a COA happens to route the zone.
 */
function getLocationIdsByZoneId(
  data: PlanningPageQuery | undefined,
): Map<ZoneId, LocationId[]> {
  const map = new Map<ZoneId, LocationId[]>();

  for (const zone of compact(data?.allZones?.nodes ?? [])) {
    map.set(
      zone.id,
      compact(zone.zoneLocationsByZoneId.nodes).map((zl) => zl.locationId),
    );
  }

  return map;
}

export function getChartererPortCostIndexByCoaId(
  data: PlanningPageQuery | undefined,
  defaultCurrency: Currency,
): ChartererPortCostIndexByCoaId {
  const map: ChartererPortCostIndexByCoaId = new Map();
  const locationIdsByZoneId = getLocationIdsByZoneId(data);

  for (const coa of data?.allCoaContracts?.nodes ?? []) {
    if (!coa) continue;

    const chartererPortCostNodes = compact(
      coa.coaContractChartererPortCostsByCoaContractId?.nodes ?? [],
    );
    if (chartererPortCostNodes.length === 0) continue;

    // Drop rows with no amount (mirrors deviation dropping null lump sums); the
    // inline null-check narrows amount so the BigFloat→number coercion is safe.
    const rows: ChartererPortCostRow[] = compact(
      chartererPortCostNodes.map((n) => {
        if (n.amount == null) return null;

        // The target is a port XOR a zone; a zone charges every port it holds.
        const locationIds =
          n.zoneId != null
            ? (locationIdsByZoneId.get(n.zoneId) ?? [])
            : n.locationId != null
              ? [n.locationId]
              : null;
        if (locationIds === null) return null;

        return {
          locationIds,
          // side is a free-form DB string; narrow to the load/discharge union
          side: n.side === 'load' ? 'load' : 'discharge',
          amount: +n.amount,
        };
      }),
    );

    map.set(coa.id, {
      rows,
      currency: coa.currency ?? defaultCurrency,
    });
  }

  return map;
}

export function getVoyageCharterPnlMaps(
  orderById: Map<number, ScenarioOrder>,
  shipownerOrgId: number | null,
) {
  const incomingVoyageChartersByOrderId: PnlData['incomingVoyageChartersByOrderId'] =
    new Map();
  const incomingVoyageChartersById: PnlData['incomingVoyageChartersById'] =
    new Map();
  const outgoingVoyageChartersById: PnlData['outgoingVoyageChartersById'] =
    new Map();

  for (const o of orderById.values()) {
    const incomingVc =
      o.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId;
    if (incomingVc && incomingVc.shipownerOrgId === shipownerOrgId) {
      incomingVoyageChartersByOrderId.set(o.id, incomingVc);
      incomingVoyageChartersById.set(incomingVc.id, incomingVc);
    }
    const outgoingVc =
      o.outgoingVoyageCharterRelation?.voyageCharterByVoyageCharterId;
    if (outgoingVc) {
      outgoingVoyageChartersById.set(outgoingVc.id, outgoingVc);
    }
  }

  return {
    incomingVoyageChartersByOrderId,
    incomingVoyageChartersById,
    outgoingVoyageChartersById,
  };
}
